import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Radios,
  Button,
  Fieldset,
  Details,
  ErrorSummary,
} from 'govuk-react-jsx';

import {
  getAccountLandLordType,
  setAccountLandLordType,
} from '../../features/registration/registrationJourneySlice';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import { updateAccount } from '../../firebase';
import useTitle from '../../hooks/useTitle';

const LandlordType = () => {
  const landlordTypeSelectedRedux = useSelector(getAccountLandLordType);
  const dispatch = useDispatch();
  const history = useHistory();
  const [hasError, setHasError] = useState(false);
  const [errorList, setErrorList] = useState([]);
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  useTitle(
    'landlordReg.LandlordType.s18',
    'What type of landlord are you? - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const handleSelection = (event) => {
    dispatch(setAccountLandLordType(event.target.value));
  };

  const errorMsg = copyProvider.getCopy(
    'landlordReg.LandlordType.s2',
    'Select an option'
  );

  const handleBtnClick = () => {
    if (!landlordTypeSelectedRedux) {
      setHasError(true);
      setErrorList([{ children: errorMsg, href: '#Private' }]);
    } else {
      let landlordType = '';
      if (
        landlordTypeSelectedRedux ===
        'Property factor or a social housing provider1'
      ) {
        landlordType = 'Property factor or a social housing provider';
      } else if (
        landlordTypeSelectedRedux ===
        'Property factor or a social housing provider2'
      ) {
        landlordType = 'Property factor or a social housing provider';
      } else {
        landlordType = landlordTypeSelectedRedux;
      }
      dispatch(setAccountLandLordType(landlordType));
      updateAccount({
        landlordType,
      })
        .then(() => {
          setHasError(false);
          if (landlordTypeSelectedRedux === 'Private Landlord') {
            history.push('/landlordReg/linked-enterprise');
          } else {
            history.push('/landlordReg/company-registration-number');
          }
        })
        .catch(() => {
          setHasError(true);
        });
    }
  };
  const pageModel = (
    <>
      {errorList?.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <Fieldset
        legend={{
          children: copyProvider.getCopy(
            'landlordReg.LandlordType.s1',
            'What type of landlord are you?'
          ),
          className:
            'govuk-fieldset__legend govuk-fieldset__legend--xl govuk-!-margin-bottom-7',
          isPageHeading: true,
        }}
      >
        <Radios
          name="landlordType"
          onChange={(event) => handleSelection(event)}
          value={landlordTypeSelectedRedux}
          errorMessage={
            hasError && {
              children: errorMsg,
            }
          }
          idPrefix="landlordType"
          items={[
            {
              children: [
                <span key="1" className="govuk-!-margin-bottom-1">
                  {copyProvider.getCopy(
                    'landlordReg.LandlordType.s3',
                    'Private landlord'
                  )}
                </span>,
              ],
              value: 'Private Landlord',
              id: 'Private',
            },
            {
              children: [
                <span key="2" className="govuk-!-margin-bottom-1">
                  {copyProvider.getCopy(
                    'landlordReg.LandlordType.s4',
                    'Public authority'
                  )}
                </span>,
              ],
              value: 'Public Authority',
              id: 'Public',
            },
            {
              children: [
                <span key="3" className="govuk-!-margin-bottom-1">
                  {copyProvider.getCopy(
                    'landlordReg.LandlordType.s5a',
                    'Registered provider of social housing'
                  )}
                </span>,
              ],
              value: 'Property factor or a social housing provider1',
              id: 'Social',
            },
            {
              children: [
                <span key="3" className="govuk-!-margin-bottom-1">
                  {copyProvider.getCopy(
                    'landlordReg.LandlordType.s5b',
                    'Property factor (Scotland)'
                  )}
                </span>,
              ],
              value: 'Property factor or a social housing provider2',
              id: 'Scotland',
            },
          ]}
        />
      </Fieldset>

      <Details
        summaryChildren={copyProvider.getCopy(
          'landlordReg.LandlordType.s7',
          'Property factor or a social housing provider'
        )}
      >
        <p>
          {copyProvider.getCopy('landlordReg.LandlordType.s8', 'A ')}
          <strong>
            {copyProvider.getCopy(
              'landlordReg.LandlordType.s9',
              'public authority'
            )}
          </strong>
          {copyProvider.getCopy(
            'landlordReg.LandlordType.s10',
            'is a government organisation that carries out tasks in the public interest or exercises the official authority it has been given. This includes government departments and their agencies, the Armed Forces, local government, the NHS and emergency services.'
          )}
        </p>
        <p>
          {copyProvider.getCopy(
            'landlordReg.LandlordType.s11',
            'When OZEV is considering eligibility, it checks the public authorities listed in Section 33 of the LGA 2003, the Freedom of Information Act, Schedule 1, parts 1-3.'
          )}
        </p>
        <p>
          {copyProvider.getCopy(
            'landlordReg.LandlordType.s12',
            'If you are still unsure you can '
          )}
          <a
            href="https://www.ons.gov.uk/economy/nationalaccounts/uksectoraccounts/datasets/publicsectorclassificationguide"
            target="_blank"
            rel="noreferrer"
          >
            {copyProvider.getCopy(
              'landlordReg.LandlordType.s13',
              'find out if you fall into this category (opens in new tab).'
            )}
          </a>
        </p>

        <p>
          {copyProvider.getCopy('landlordReg.LandlordType.s8', 'A ')}
          <strong>
            {copyProvider.getCopy(
              'landlordReg.LandlordType.s14',
              'registered social housing provider '
            )}
          </strong>
          {copyProvider.getCopy(
            'landlordReg.LandlordType.s15',
            'A registered provider of social housing is an organisation that is registered with the government and provides, or intends to provide, social housing.'
          )}
        </p>
      </Details>
      <Button className="govuk-button" onClick={handleBtnClick}>
        {copyProvider.getCopy('landlordReg.LandlordType.s6', 'Continue')}
      </Button>
    </>
  );

  return pageModel;
};

export default LandlordType;
